import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, HttpError } from "../api";
import { useAuth } from "../auth";
import type {
  AttachPdfResponse,
  Batch,
  BatchImportResponse,
  OzonCabinet,
  OzonFreeStockRow,
} from "../types";
import { formatMSK } from "../utils/dates";

export default function Batches() {
  const { user } = useAuth();
  const [items, setItems] = useState<Batch[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [labelPdfs, setLabelPdfs] = useState<File[]>([]);
  const [comment, setComment] = useState("");
  // Кабинет Ozon (юрлицо) партии — у каждого юрлица свои коды TechShare.
  const [cabinets, setCabinets] = useState<OzonCabinet[]>([]);
  const [clientId, setClientId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [lastImport, setLastImport] = useState<BatchImportResponse | null>(null);
  const [lastAttach, setLastAttach] = useState<AttachPdfResponse | null>(null);
  const [stock, setStock] = useState<OzonFreeStockRow[] | null>(null);
  const [pdfBusyId, setPdfBusyId] = useState<number | null>(null);
  const [pdfErr, setPdfErr] = useState<string | null>(null);

  const cabinetName = (cid: number | null): string | null =>
    cid == null ? null : cabinets.find((c) => c.ozon_client_id === cid)?.name ?? `#${cid}`;

  async function refresh() {
    try {
      setItems(await api.listBatches());
      setStock(await api.listFreeStock());
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    }
  }

  useEffect(() => {
    refresh();
    api
      .listCabinets()
      .then((list) => {
        setCabinets(list);
        if (list.length === 1) setClientId(String(list[0].ozon_client_id));
      })
      .catch(() => setCabinets([]));
  }, []);

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    setFile(e.target.files?.[0] ?? null);
  }

  async function onUpload(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setErr(null);
    setLastImport(null);
    setLastAttach(null);
    try {
      const r = await api.uploadBatch(
        file,
        comment || undefined,
        labelPdfs,
        clientId ? Number(clientId) : undefined,
      );
      setLastImport(r);
      setFile(null);
      setLabelPdfs([]);
      setComment("");
      (document.getElementById("batch-file") as HTMLInputElement | null)?.value &&
        ((document.getElementById("batch-file") as HTMLInputElement).value = "");
      (document.getElementById("batch-label-pdf") as HTMLInputElement | null)?.value &&
        ((document.getElementById("batch-label-pdf") as HTMLInputElement).value = "");
      await refresh();
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой загрузки");
    } finally {
      setBusy(false);
    }
  }

  const canUpload = user?.role === "admin" || user?.role === "operator";

  return (
    <div className="container">
      <h1>Партии кодов</h1>

      {canUpload && (
        <div className="card">
          <h2>Загрузить CSV от TechShare</h2>
          <form onSubmit={onUpload}>
            <div className="row">
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                required
                title="юрлицо / кабинет Ozon, которому принадлежат коды этой партии"
              >
                <option value="" disabled>
                  юрлицо *
                </option>
                {cabinets.map((c) => (
                  <option key={c.ozon_client_id} value={c.ozon_client_id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <input id="batch-file" type="file" accept=".csv,text/csv" onChange={onFile} required />
              <input
                type="text"
                placeholder="комментарий (необязательно)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="grow"
              />
              <button
                className="primary"
                type="submit"
                disabled={!file || busy || (cabinets.length > 0 && !clientId)}
              >
                {busy ? "..." : "Загрузить"}
              </button>
            </div>
            <div className="row" style={{ marginTop: 8, alignItems: "center", gap: 8 }}>
              <label className="grow">
                <div className="muted" style={{ fontSize: 12 }}>
                  PDF с этикетками партии — можно выбрать несколько файлов
                  (Ctrl/⌘ + клик), необязательно; докачать можно позже.
                </div>
                <input
                  id="batch-label-pdf"
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  onChange={(e) => setLabelPdfs(Array.from(e.target.files ?? []))}
                />
                {labelPdfs.length > 0 && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    выбрано: {labelPdfs.map((f) => f.name).join(", ")}
                  </div>
                )}
              </label>
            </div>
          </form>
          {err && <div className="error">{err}</div>}
          {lastImport && (
            <div className="ok" style={{ marginTop: 12 }}>
              Импортировано: <b>{lastImport.imported}</b>, пропущено (уже есть в
              системе): {lastImport.duplicates}, ошибки парсинга:{" "}
              {lastImport.errors.length}
            </div>
          )}
          {canUpload && (
            <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
              Если TechShare прислал допзаказ одним файлом со старыми кодами — загрузите как
              есть: старые строки пропустятся, новые добавятся в новую партию.
            </p>
          )}
        </div>
      )}

      {canUpload && items && items.length > 0 && (
        <AttachHistoricPdfCard
          batches={items}
          lastAttach={lastAttach}
          setLastAttach={setLastAttach}
          onDone={refresh}
        />
      )}

      <div className="card">
        <h2>Список партий</h2>
        <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
          Колонка PDF — исходные этикетки TechShare, которые заливает оператор.
          Склад скачивает их отсюда (один файл → PDF, несколько → ZIP).
        </p>
        {items === null ? (
          <div>Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="muted">Пусто. Загрузите первую партию.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>id</th>
                <th>Имя файла</th>
                <th>Юрлицо</th>
                <th>Статус</th>
                <th>PDF</th>
                <th className="right">Всего</th>
                <th className="right">Свободно</th>
                <th>Загружен</th>
                <th>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id}>
                  <td>{b.id}</td>
                  <td className="mono">{b.source_filename}</td>
                  <td className="muted">{cabinetName(b.ozon_client_id) || "—"}</td>
                  <td>
                    <span className={`status status-${b.status}`}>{b.status}</span>
                  </td>
                  <td>
                    <BatchPdfCell
                      batch={b}
                      busyId={pdfBusyId}
                      err={pdfErr}
                      onBusy={setPdfBusyId}
                      onErr={setPdfErr}
                    />
                  </td>
                  <td className="right">{b.codes_total}</td>
                  <td className="right">{b.codes_free}</td>
                  <td className="muted">{formatMSK(b.created_at)}</td>
                  <td className="muted">{b.comment || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {pdfErr && <div className="error" style={{ marginTop: 8 }}>{pdfErr}</div>}
      </div>

      <ArticleStockBlock stock={stock} onPrinted={refresh} />
      <UnmatchedStockBlock stock={stock} />
    </div>
  );
}

function BatchPdfCell({
  batch,
  busyId,
  onBusy,
  onErr,
}: {
  batch: Batch;
  busyId: number | null;
  err: string | null;
  onBusy: (id: number | null) => void;
  onErr: (msg: string | null) => void;
}) {
  const hasPdf = Boolean(batch.label_pdf_path);
  if (!hasPdf) {
    return <span className="muted">—</span>;
  }
  const pages =
    batch.label_pdf_matched != null && batch.label_pdf_pages != null
      ? `${batch.label_pdf_matched}/${batch.label_pdf_pages}`
      : "✓";

  async function onDownload() {
    onErr(null);
    onBusy(batch.id);
    try {
      const { blob, filename } = await api.downloadBatchLabelPdfs(batch.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      onErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой скачивания PDF");
    } finally {
      onBusy(null);
    }
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
      <span className="ok" title="PDF этикеток привязан к партии">
        PDF {pages}
      </span>
      <button
        type="button"
        onClick={onDownload}
        disabled={busyId !== null}
        title="Скачать исходные PDF этикеток (для склада)"
      >
        {busyId === batch.id ? "…" : "Скачать"}
      </button>
    </span>
  );
}

/** Временная форма догрузки PDF-этикеток в **существующую** партию — нужна для
 *  исторических партий, которые были загружены до появления фичи. При выборе
 *  партии отображается, есть ли у неё уже привязанный PDF (чтобы админ понимал,
 *  что повторная заливка перезапишет прежние привязки). */
function AttachHistoricPdfCard({
  batches,
  lastAttach,
  setLastAttach,
  onDone,
}: {
  batches: Batch[];
  lastAttach: AttachPdfResponse | null;
  setLastAttach: (r: AttachPdfResponse | null) => void;
  onDone: () => Promise<void>;
}) {
  const [batchId, setBatchId] = useState<number | "">("");
  const [pdfs, setPdfs] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const selected = batches.find((b) => b.id === batchId) ?? null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!batchId || pdfs.length === 0) return;
    setBusy(true);
    setErr(null);
    setLastAttach(null);
    try {
      const r = await api.attachBatchPdf(Number(batchId), pdfs);
      setLastAttach(r);
      setPdfs([]);
      setBatchId("");
      await onDone();
    } catch (e2) {
      setErr(e2 instanceof HttpError ? `${e2.status}: ${e2.message}` : "сбой");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>
        Залить PDF с этикетками в партию{" "}
        <span className="muted" style={{ fontSize: 12 }}>
          (для исторических партий без PDF)
        </span>
      </h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Выберите партию → один или несколько PDF из TechShare → система
        распознает страницы и привяжет каждую к соответствующему коду. Если
        в партии уже был PDF — заливка его перезапишет.
      </p>
      <form onSubmit={onSubmit}>
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          <select
            value={batchId}
            onChange={(e) => setBatchId(e.target.value ? Number(e.target.value) : "")}
            disabled={busy}
            required
          >
            <option value="">— выберите партию —</option>
            {batches.map((b) => (
              <option key={b.id} value={b.id}>
                #{b.id} · {b.source_filename} · всего {b.codes_total}
                {b.label_pdf_path ? " · PDF ✓" : " · PDF ✗"}
              </option>
            ))}
          </select>
          <input
            type="file"
            accept=".pdf,application/pdf"
            multiple
            onChange={(e) => setPdfs(Array.from(e.target.files ?? []))}
            disabled={busy}
            required
          />
          <button
            className="primary"
            type="submit"
            disabled={!batchId || pdfs.length === 0 || busy}
          >
            {busy ? "..." : `Залить PDF${pdfs.length > 1 ? ` (${pdfs.length})` : ""}`}
          </button>
        </div>
      </form>
      {pdfs.length > 0 && (
        <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
          выбрано: {pdfs.map((f) => f.name).join(", ")}
        </div>
      )}
      {selected && selected.label_pdf_path && (
        <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          В выбранной партии уже есть PDF ({selected.label_pdf_matched ?? 0} из{" "}
          {selected.label_pdf_pages ?? 0} страниц привязано) — заливка заменит его.
        </div>
      )}
      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      {lastAttach && (
        <div className="ok" style={{ marginTop: 12 }}>
          Привязано: <b>{lastAttach.total_matched}</b> из {lastAttach.total_pages} страниц.
          <ul style={{ margin: "4px 0 0 18px" }}>
            {lastAttach.files.map((f) => (
              <li key={f.filename}>
                <span className="mono">{f.filename}</span>: {f.matched}/{f.pages}
                {f.unmatched_pages.length > 0 && (
                  <>
                    , не привязано: <span className="mono">{f.unmatched_pages.join(", ")}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** «100700_черный_140» → «100700_черный». Если второго `_` нет — возвращаем
 *  артикул как есть (товар без размерной сетки). */
function groupPrefixOf(article: string): string {
  const i = article.lastIndexOf("_");
  return i <= 0 ? article : article.slice(0, i);
}

interface ArticleGroup {
  prefix: string;
  totalFree: number;
  items: OzonFreeStockRow[]; // конкретные размеры/варианты внутри группы
  name: string | null; // показываем имя любого из вариантов — они обычно схожи
}

interface ArticleGroupExt extends ArticleGroup {
  totalPrintable: number;
}

function buildGroups(rows: OzonFreeStockRow[]): ArticleGroupExt[] {
  const byPrefix = new Map<string, ArticleGroupExt>();
  for (const r of rows) {
    if (r.article === null) continue;
    const p = groupPrefixOf(r.article);
    const g = byPrefix.get(p);
    if (g) {
      g.totalFree += r.free_count;
      g.totalPrintable += r.printable_count;
      g.items.push(r);
      g.name = g.name ?? r.name;
    } else {
      byPrefix.set(p, {
        prefix: p,
        totalFree: r.free_count,
        totalPrintable: r.printable_count,
        items: [r],
        name: r.name,
      });
    }
  }
  // Внутри каждой группы — сортировка по артикулу (размеры по порядку),
  // сами группы — по убыванию остатка.
  const groups = Array.from(byPrefix.values());
  for (const g of groups) g.items.sort((a, b) => (a.article ?? "").localeCompare(b.article ?? ""));
  groups.sort((a, b) => b.totalFree - a.totalFree || a.prefix.localeCompare(b.prefix));
  return groups;
}

function ArticleStockBlock({
  stock,
  onPrinted,
}: {
  stock: OzonFreeStockRow[] | null;
  onPrinted: () => Promise<void>;
}) {
  const { user } = useAuth();
  const canPrint = user?.role === "admin" || user?.role === "operator";
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [printingBarcode, setPrintingBarcode] = useState<string | null>(null);
  const [printErr, setPrintErr] = useState<string | null>(null);
  const groups = stock ? buildGroups(stock) : null;
  const totalCodes = groups ? groups.reduce((s, g) => s + g.totalFree, 0) : 0;
  const totalPrintable = groups ? groups.reduce((s, g) => s + g.totalPrintable, 0) : 0;

  async function onPrint(row: OzonFreeStockRow) {
    setPrintErr(null);
    if (row.printable_count <= 0) {
      setPrintErr(
        `У ${row.article} нет кодов, готовых к печати — залейте PDF маркировки в соответствующую партию.`,
      );
      return;
    }
    const raw = window.prompt(
      `Сколько этикеток ${row.article} напечатать?\nДоступно: ${row.printable_count}`,
      String(Math.min(row.printable_count, 10)),
    );
    if (!raw) return;
    const qty = Number(raw);
    if (!Number.isInteger(qty) || qty <= 0) {
      setPrintErr("Укажите целое положительное число");
      return;
    }
    if (qty > row.printable_count) {
      setPrintErr(`Запрошено ${qty}, доступно всего ${row.printable_count}`);
      return;
    }
    setPrintingBarcode(row.barcode);
    try {
      const { blob, filename } = await api.printLabels(row.barcode, qty);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      await onPrinted();
    } catch (e) {
      setPrintErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой печати");
    } finally {
      setPrintingBarcode(null);
    }
  }

  function toggle(prefix: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });
  }

  return (
    <div className="card">
      <h2>
        Артикулы{" "}
        {groups && (
          <span className="muted">
            ({groups.length} групп · {totalCodes} свободных · {totalPrintable} к печати)
          </span>
        )}
      </h2>
      <p className="muted">
        Схлопнуто по «артикул_цвет» — размеры раскрываются по{" "}
        <span className="mono">+</span>. Привязка GTIN → артикул берётся из
        Ozon-справочника. «К печати» — свободные коды, у которых есть страница
        в PDF этикеток партии; это подмножество «Свободно», печать не уменьшает
        количество «Свободно».
      </p>
      {printErr && <div className="error" style={{ marginBottom: 8 }}>{printErr}</div>}
      {groups === null ? (
        <div>Загрузка…</div>
      ) : groups.length === 0 ? (
        <div className="muted">
          Нет свободных кодов, привязанных к артикулам Ozon. Возможно, ещё не
          загружен <span className="mono">ozn_tov.csv</span>.
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th style={{ width: 28 }}></th>
              <th>Артикул</th>
              <th>Название</th>
              <th className="right">Вариантов</th>
              <th className="right">Свободно</th>
              <th className="right">К печати</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const open = expanded.has(g.prefix);
              const rowKey = g.prefix;
              const clickable = g.items.length > 1;
              return (
                <>
                  <tr
                    key={rowKey}
                    onClick={clickable ? () => toggle(g.prefix) : undefined}
                    style={{ cursor: clickable ? "pointer" : "default" }}
                  >
                    <td className="mono">
                      {clickable ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggle(g.prefix);
                          }}
                          style={{ padding: "0 6px" }}
                          title={open ? "Свернуть" : "Развернуть размеры"}
                        >
                          {open ? "−" : "+"}
                        </button>
                      ) : (
                        " "
                      )}
                    </td>
                    <td className="mono">
                      <b>{g.prefix}</b>
                    </td>
                    <td>{g.name || <span className="muted">—</span>}</td>
                    <td className="right">{g.items.length}</td>
                    <td className="right">
                      <b>{g.totalFree}</b>
                    </td>
                    <td className="right">{g.totalPrintable}</td>
                    <td></td>
                  </tr>
                  {open &&
                    g.items.map((row) => (
                      <tr
                        key={`${rowKey}::${row.barcode}`}
                        style={{ background: "#fafafa" }}
                      >
                        <td></td>
                        <td
                          className="mono muted"
                          style={{ paddingLeft: 28, fontSize: 12 }}
                        >
                          {row.article}
                        </td>
                        <td
                          className="muted"
                          style={{ fontSize: 12 }}
                          title={`GTIN: ${row.barcode}`}
                        >
                          <span className="mono">{row.barcode}</span>
                        </td>
                        <td></td>
                        <td className="right">{row.free_count}</td>
                        <td className="right">{row.printable_count}</td>
                        <td className="right">
                          {canPrint && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onPrint(row);
                              }}
                              disabled={printingBarcode !== null || row.printable_count === 0}
                              title={
                                row.printable_count === 0
                                  ? "Нет кодов, готовых к печати (нужно залить PDF этикеток в партию)"
                                  : `Доступно к печати: ${row.printable_count}`
                              }
                            >
                              🖨 Скачать коды
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                </>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function UnmatchedStockBlock({ stock }: { stock: OzonFreeStockRow[] | null }) {
  const unmatched = stock ? stock.filter((s) => s.article === null) : null;
  const total = unmatched ? unmatched.reduce((s, r) => s + r.free_count, 0) : 0;

  return (
    <div className="card">
      <h2>
        GTIN без артикулов{" "}
        {unmatched && (
          <span className="muted">
            ({unmatched.length} GTIN · {total} свободных кодов)
          </span>
        )}
      </h2>
      <p className="muted">
        Эти штрихкоды есть на складе, но их нет в Ozon-справочнике — залейте
        обновлённый <span className="mono">ozn_tov.csv</span> в{" "}
        <Link to="/ozon">Ozon-справочник</Link>, и они подтянутся автоматически.
      </p>
      {unmatched === null ? (
        <div>Загрузка…</div>
      ) : unmatched.length === 0 ? (
        <div className="ok">Все свободные коды матчатся с Ozon-справочником ✓</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Штрихкод (GTIN)</th>
              <th className="right">Свободно</th>
            </tr>
          </thead>
          <tbody>
            {unmatched.map((r) => (
              <tr key={r.barcode}>
                <td className="mono">{r.barcode}</td>
                <td className="right">{r.free_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
